import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { SettingsContext } from "../../../../../settingsContext";
import RichText from "../../../../CmsComponents/RichText/RichText";
import { IButton } from "../Button/Button";
import { BNBox, BNButton } from "@bn-monorepo/js-packages";
import { syncRangeInputToOutput } from "./syncRangeInputToOutput";

interface Mark {
  value: number;
  label: string;
}

export interface ISlider {
  min: number;
  max: number;
  default: number;
  unitSymbol?: string;
  button?: IButton;
  queryParameterName?: string;
  step: number;
  ariaLabel: string;
  richText?: string;
  marks?: boolean | Mark[];
  title?: string;
  decimals?: number;
  hideCurrentValueLabel?: boolean | undefined;
  buttonClickHandler?: (newValue) => void;
  onChangeHandler?: (newValue) => void;
}

const SliderSsrRenderOnly: ReactFCWithNoJs<ISlider> = (props) => {
  const {
    ariaLabel,
    min,
    max,
    default: value,
    unitSymbol,
    button,
    queryParameterName,
    richText,
    step,
    marks,
    title,
    decimals,
    hideCurrentValueLabel,
  } = props;

  const { locale } = useContext(SettingsContext);
  const [searchParams] = useSearchParams();
  const loanAmount = searchParams.get("loanamount");

  const getValidNumberValue = (value: number, min: number, max: number) => {
    if (isNaN(Number(value))) {
      return props.default;
    } else {
      return value >= min && value <= max ? value : value < min ? min : max;
    }
  };

  const initialValue = loanAmount
    ? getValidNumberValue(Number(loanAmount), min, max)
    : value > 0
      ? value
      : (min - max) / 2;

  const format = (value: number, maxDecimals: number = 0) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: maxDecimals,
    }).format(value);
  const [sliderId] = useState("slider-" + Math.floor(Math.random() * 10000));

  useEffect(() => {
    // In case of SPA navigation
    const cleanup = syncRangeInputToOutput(sliderId);
    return cleanup;
  }, [sliderId]);

  return (
    <form method="get" action={button?.href}>
      {title ? (
        <BNBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "2rem",
          }}
        >
          <div>{title}</div>
          <div>
            <output htmlFor={sliderId}>{format(initialValue, decimals)}</output> {unitSymbol}
          </div>
        </BNBox>
      ) : hideCurrentValueLabel ? null : (
        <BNBox
          component={"p"}
          sx={{
            margin: 0,
            fontSize: "4rem",
            textAlign: "center",
            fontWeight: "normal",
            whiteSpace: "nowrap",
          }}
        >
          <output htmlFor={sliderId}>{format(initialValue, decimals)}</output> {unitSymbol}
        </BNBox>
      )}
      <BNBox
        sx={(theme) => ({
          width: "100%",
          '& input[type="range"]': {
            WebkitAppearance: "none",
            appearance: "none",
            width: "100%",
            height: "1.4rem",
            background: theme.vars.palette.background.default,
            outline: "none",
            opacity: 0.7,
            transition: "opacity .2s",
            borderRadius: "0.7rem",
            padding: 0,
            margin: 0,
            color: theme.vars.palette.primary.darker,
            "&:hover": { opacity: 1 },
            "&::-webkit-slider-thumb": {
              WebkitAppearance: "none",
              appearance: "none",
              width: "3.2rem",
              height: "3.2rem",
              background: theme.vars.palette.secondary.light,
              cursor: "pointer",
              borderRadius: "50%",
              border: `0.5rem solid ${theme.vars.palette.secondary.dark}`,
              transition: "box-shadow .15s",
            },
            "&::-moz-range-thumb": {
              width: "3.2rem",
              height: "3.2rem",
              background: theme.vars.palette.secondary.light,
              cursor: "pointer",
              borderRadius: "50%",
              border: `0.5rem solid ${theme.vars.palette.secondary.dark}`,
              transition: "box-shadow .15s",
            },
            "&:focus::-webkit-slider-thumb, &:focus-visible::-webkit-slider-thumb": {
              boxShadow: `0 0 0 0.2rem ${theme.vars.palette.secondary.dark}`,
            },
            "&:focus::-moz-range-thumb, &:focus-visible::-moz-range-thumb": {
              boxShadow: `0 0 0 0.2rem ${theme.vars.palette.secondary.dark}`,
            },
          },
        })}
      >
        <input
          type="range"
          name={queryParameterName}
          min={min}
          max={max}
          defaultValue={initialValue}
          step={step}
          id={sliderId}
          aria-label={ariaLabel}
        />
      </BNBox>
      {marks == null && (
        <BNBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>{format(min)}</div>
          <div>{format(max)}</div>
        </BNBox>
      )}
      {richText && <RichText html={richText} />}
      {button && (
        <BNBox sx={{ textAlign: "center" }}>
          <BNButton
            sx={{
              minHeight: "5rem",
              px: 4,
              fontSize: "1.6rem !important",
              maxWidth: "100%",
            }}
            type="submit"
          >
            {button?.text}
          </BNButton>
        </BNBox>
      )}
    </form>
  );
};

SliderSsrRenderOnly.noJsRequired = true;
export default SliderSsrRenderOnly;
